import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PiggyBank, Timer } from "lucide-react";

const TARGET = new Date("2026-10-12T08:00:00-05:00").getTime();
const ENROLL_URL = "https://bit.ly/lssp-fall-enrollment-form";

const plans = [
  {
    name: "Group Lessons",
    ages: "Ages 2.5–10",
    price: 215,
    normal: 250,
    blurb: "Small-group confidence builders, taught at your own pool.",
    id: "pricing-card-group",
  },
  {
    name: "Toddler Survival Private",
    ages: "Ages 1–3",
    price: 335,
    normal: 390,
    blurb: "One-on-one water-safety and survival skills for your littlest swimmer.",
    id: "pricing-card-toddler",
  },
  {
    name: "Private Lessons — Learn to Swim",
    ages: "Ages 3–8",
    price: 425,
    normal: 500,
    blurb: "Private, at-your-pace instruction with Coach Abrie and her team.",
    id: "pricing-card-private",
  },
];

export default function Pricing() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, TARGET - now);
  const units = [
    { label: "days", value: Math.floor(diff / 86400000) },
    { label: "hours", value: Math.floor(diff / 3600000) % 24 },
    { label: "min", value: Math.floor(diff / 60000) % 60 },
    { label: "sec", value: Math.floor(diff / 1000) % 60 },
  ];

  return (
    <section id="pricing" data-testid="pricing-section" className="scroll-mt-24 bg-[#f2ebd9]">
      <div className="mx-auto max-w-6xl px-4 sm:px-8 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between"
        >
          <div className="max-w-xl">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#c97049]">
              Resident pricing · 15% off everything
            </p>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-[#00334a]">
              Your neighbor rate for the 6-week mini session
            </h2>
          </div>
          <div data-testid="enrollment-countdown">
            <p className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-[#00334a]/70">
              <Timer className="h-4 w-4 text-[#c97049]" />
              Fall session begins Monday, October 12
            </p>
            <div className="mt-3 flex gap-2.5">
              {units.map((u) => (
                <div key={u.label} className="min-w-[60px] rounded-2xl bg-[#00334a] px-3 py-2.5 text-center shadow-md">
                  <span className="block font-serif text-2xl sm:text-3xl font-bold tabular-nums text-[#f8f3e9]">
                    {String(u.value).padStart(2, "0")}
                  </span>
                  <span className="block text-[9px] uppercase tracking-[0.22em] text-[#f8f3e9]/60">{u.label}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {plans.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.12 }}
              data-testid={p.id}
              className="card-lift flex h-full flex-col rounded-3xl bg-white border border-[#00334a]/10 p-7 shadow-sm"
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#87856c]">Resident price</p>
              <h3 className="mt-2 font-serif text-xl sm:text-2xl font-semibold text-[#00334a]">{p.name}</h3>
              <p className="mt-1 text-xs sm:text-sm font-medium text-[#52656d]">{p.ages}</p>
              <p className="mt-5 flex items-baseline gap-3">
                <span className="font-serif text-4xl sm:text-5xl font-bold text-[#00334a]">${p.price}</span>
                <span className="text-sm text-[#52656d]">
                  <s>normally ${p.normal}</s>
                </span>
              </p>
              <span className="mt-3 inline-flex w-fit items-center rounded-full bg-[#e59a6d]/20 px-3 py-1 text-xs font-bold text-[#c97049]">
                You save ${p.normal - p.price} per session
              </span>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-[#00334a]/70">{p.blurb}</p>
              <a
                href={ENROLL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`pricing-enroll-link-${p.id.replace("pricing-card-", "")}`}
                className="mt-6 text-sm font-bold text-[#c97049] underline underline-offset-4 decoration-[#c97049]/40 hover:text-[#00334a] hover:decoration-[#00334a]/40 transition-colors duration-300"
              >
                Enroll at the resident rate →
              </a>
            </motion.article>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.15 }}
          data-testid="resident-savings-banner"
          className="mt-10 flex items-center justify-center gap-3 rounded-3xl bg-[#e7b576]/40 border border-[#e7b576] px-6 py-5 text-center text-sm sm:text-base font-semibold text-[#00334a]"
        >
          <PiggyBank className="h-6 w-6 shrink-0 text-[#c97049]" />
          Residents save up to $600 per year compared to public pricing — automatically, just for living here.
        </motion.p>
      </div>
    </section>
  );
}
